import { useSnackbar } from 'notistack';
import { Dispatch, useEffect, useRef, useState } from 'react';
import {
  updateSiteProcess,
  getSiteProcessDetail
} from '../../../../apicalls/process';
import { TSelectItem } from '../../../../types/common';
import { TSiteProcessDetailData } from '../../../../types/process';
import { TSiteProcessDetailApiResponse } from '../../../../types/process';
import { checkResData } from '../../../../utils/common';
import {
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue
} from 'react-hook-form';
import {
  convertApiDateToFormDate,
  convertFormDateToApiDate,
  createSiteProcessAndSiteTaskEmployeeList,
  createSiteProcessFormProcessListPulldown
} from '../../../../utils';
import {
  getSiteProcessStatusName,
  createGanttResource
} from '../../../../utils';
import { parseNumber } from '../../../../utils/processAndTaskFormUtils';
import { TEditSiteProcessForm } from '../../../../types/process';
import { siteProcessGanttChartResource } from '../../../../GlobalState';
import { useRecoilState } from 'recoil';

export type Props = {
  setIsOpen: Dispatch<boolean>;
  afterUpdate: (updatedSiteProcessDetailData: TSiteProcessDetailData) => void;
  afterDelete: () => void; // 削除モーダル用
  siteProcessId: number;
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
  reset: UseFormReset<any>;
  isOpen: boolean;
};

export const useHooks = (props: Props) => {
  const { setIsOpen, afterUpdate, afterDelete, reset, isOpen, siteProcessId } =
    props;

  // 送信可能フラグ 多重送信対策
  const isSubmitable = useRef(false);

  // 削除モーダル用
  const [isOpenSiteProcessDeleteModal, setIsOpenSiteProcessDeleteModal] =
    useState(false);
  const [, setAddGanttResourceData] = useRecoilState(
    siteProcessGanttChartResource
  );
  // 削除モーダルボタン押下時のハンドラ
  const handleDeleteSubmit = () => {
    setIsOpenSiteProcessDeleteModal(true);
  };
  // 削除後のハンドラ
  const handleAfterDelete = () => {
    afterDelete();
    setIsOpen(false); // 工程削除時に編集モーダルも閉じる
  };

  // 工程編集の処理
  const update = (form: TEditSiteProcessForm) => {
    if (!isSubmitable.current) return;

    isSubmitable.current = false;

    updateSiteProcess(
      form.site_process_id,
      form.site_process_name,
      form.construct_id,
      convertFormDateToApiDate(form.start_date) as string, // 開始日は必須項目なので文字列として扱う
      form.status.value,
      form.parent_site_process_id.value,
      form.charge_user_id?.value,
      convertFormDateToApiDate(form.end_date),
      form.planned_man_day,
      form.actual_man_day,
      form.note
    ).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const ganttResource = createGanttResource(data.data);
          setAddGanttResourceData([ganttResource]);
          setIsOpen(false);
          afterUpdate(data.data);
        } else {
          isSubmitable.current = true;
        }
      });
    });
  };

  // キャンセル時の処理
  const cancelUpdate = () => {
    setIsOpen(false);
  };

  // 大工程選択リスト
  const [parentSiteProccessList, setParentSiteProccessList] = useState<
    TSelectItem[]
  >([]);

  // 担当者選択リスト
  const [employeeList, setEmployeeList] = useState<TSelectItem[]>([]);

  // 工程詳細データ
  const [siteProcessDetail, setSiteProcessDetail] =
    useState<TSiteProcessDetailData>();

  // ステータス選択リスト(セレクトフィールドで選択した時の値)
  const statusList = Object.freeze({
    data: [
      { value: 1, label: '未着手' },
      { value: 2, label: '着手中' },
      { value: 3, label: '完了' },
      { value: 4, label: '実施不要' }
    ]
  });

  // 期間終了日付
  const [endDate, setEndDate] = useState('');

  // 期間終了日付変更時のハンドラ
  const handleChangeEndDatePicker = (e: any) => {
    setEndDate(e.target.value);
  };

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  // モーダルを開く時に初期値を空に
  useEffect(() => {
    if (isOpen) {
      // 工程詳細を取得してフォームに表示する
      getSiteProcessDetail(siteProcessId).then(resData => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          if (data) {
            const responseSiteProcessDetail: TSiteProcessDetailData = data.data;
            setSiteProcessDetail(responseSiteProcessDetail);

            // 工程一覧プルダウン
            const constructId = responseSiteProcessDetail.constructs.id;
            createSiteProcessFormProcessListPulldown(
              constructId,
              enqueueSnackbar
            ).then(list => {
              setParentSiteProccessList(list);
            });

            // 担当者選択プルダウン
            createSiteProcessAndSiteTaskEmployeeList(
              constructId,
              enqueueSnackbar
            ).then(employeeList => {
              setEmployeeList(employeeList);
            });

            // フォームに工程詳細の内容を表示
            reset({
              parent_site_process_id: {
                value: responseSiteProcessDetail.parent_site_process?.id,
                label:
                  responseSiteProcessDetail.parent_site_process
                    ?.site_process_name
              },
              site_process_name: responseSiteProcessDetail.site_process_name,
              charge_user_id: {
                value: responseSiteProcessDetail.charge_user?.id,
                label: responseSiteProcessDetail.charge_user?.name
              },
              start_date: convertApiDateToFormDate(
                responseSiteProcessDetail.start_date
              ),
              end_date: convertApiDateToFormDate(
                responseSiteProcessDetail.end_date
              ),
              planned_man_day: responseSiteProcessDetail.planned_man_day,
              actual_man_day: responseSiteProcessDetail.actual_man_day,
              note: responseSiteProcessDetail.note,
              status: {
                value: responseSiteProcessDetail.status,
                label: getSiteProcessStatusName(
                  responseSiteProcessDetail.status
                )
              },
              site_process_id: responseSiteProcessDetail.id,
              construct_id: responseSiteProcessDetail.constructs.id
            });

            // 日付のFROM-TOの整合性チェック用stateに初期値セット
            setEndDate(
              convertApiDateToFormDate(responseSiteProcessDetail.end_date) ?? ''
            );
          }
        });
      });

      // オープン時に送信可能にする
      isSubmitable.current = true;
    } else {
      // フォームの初期化
      setSiteProcessDetail(undefined);
      setParentSiteProccessList([]);
      setEmployeeList([]);
      reset({
        parent_site_process_id: { value: 0, label: '--' },
        site_process_name: null,
        charge_user_id: { value: 0, label: '--' },
        start_date: null,
        end_date: null,
        planned_man_day: null,
        actual_man_day: null,
        note: null,
        status: { value: null, label: '' },
        site_process_id: null,
        construct_id: null
      });
    }
  }, [isOpen]);

  return {
    update,
    cancelUpdate,
    parentSiteProccessList,
    employeeList,
    endDate,
    handleChangeEndDatePicker,
    siteProcessDetail,
    statusList,
    isOpenSiteProcessDeleteModal,
    setIsOpenSiteProcessDeleteModal,
    handleDeleteSubmit,
    handleAfterDelete
  };
};
